import { Chip, Grid } from '@mui/material'
import Box from '@mui/material/Box'
// import Button from '@mui/material/Button'
import Card from '@mui/material/Card'
import Typography from '@mui/material/Typography'
import LocationIcon from 'assets/icons/location.svg'
import useArtistCardStyles from 'components/common/cards/ArtistCardNew/ArtistCard.styles'
import {
  LocationFilter,
  TattooStyleFilter,
} from 'components/common/FilterBar/Filter'
import NextImage from 'next/image'
import Link from 'next/link'
import { blurDataURL } from 'styles/colors/colors'

export interface StudioData {
  city: string
  country: string
  latitude?: number
  longitude?: number
  distance?: number
}

export interface StyleData {
  id?: number
  name: string
}

export interface ArtistCardData {
  image: StaticImageData
  first_name: string
  last_name: string
  style: StyleData[]
  tattoo: {
    tattoo_uid: string
    image_medium: string | StaticImageData
  }[]
  profile_uid?: string
  studio: StudioData[]
  slug?: string
}

const sortLocationsByDistance = (
  locations: StudioData[],
  filter: LocationFilter
) => {
  const locationsCopy = [...locations]

  locationsCopy.forEach((el) => {
    el.distance = Math.sqrt(
      Math.pow(el.longitude! - parseFloat(filter.longitude), 2) +
        Math.pow(el.latitude! - parseFloat(filter.latitude), 2)
    )
  })

  locationsCopy.sort((a, b) => a.distance! - b.distance!)

  return locationsCopy
}

export const prepareLocationsData = (
  studios: StudioData[],
  locationFilter: LocationFilter | null
) => {
  let locations = ''
  if (studios) {
    if (locationFilter) {
      const sortedLocations = sortLocationsByDistance(studios, locationFilter)
      for (let i = 0; i < sortedLocations.length; i++) {
        locations =
          locations + sortedLocations[i].city + ' ' + sortedLocations[i].country
        if (i < sortedLocations.length - 1) {
          locations += ', '
        }
      }
    } else {
      for (let i = 0; i < studios.length; i++) {
        locations = locations + studios[i].city + ' ' + studios[i].country
        if (i < studios.length - 1) {
          locations += ', '
        }
      }
    }
  }

  return locations
}

export const prepareStylesData = (
  style: StyleData[],
  styleFilter: TattooStyleFilter | null
) => {
  const styleData = [...style]
  if (styleData) {
    if (styleFilter) {
      const idx = styleData.findIndex((el) => el.name === styleFilter.label)

      styleData.unshift(styleData.splice(idx, 1)[0])
    }
  }

  return styleData
}

const ArtistCard = ({
  variant,
  data,
  locationFilter,
  styleFilter,
}: {
  variant?: 'hero' | 'small'
  data: ArtistCardData
  locationFilter: LocationFilter | null
  styleFilter: TattooStyleFilter | null
}) => {
  const { image, first_name, last_name, studio, style, tattoo, slug } = data
  //   const styles = useArtistCardStyles({ variant: variant })
  const styles = useArtistCardStyles()
  const locations = prepareLocationsData(studio, locationFilter)
  const tattooStyles = prepareStylesData(style, styleFilter)

  const CardHTML = () => (
    <Card sx={styles.card}>
      <Box sx={{ height: '80%', position: 'relative' }}>
        <Grid container spacing={0.5} justifyContent="center">
          {tattoo.map((el, idx) => {
            if (idx < 2) {
              return (
                <Grid
                  item
                  xs={6}
                  key={el.tattoo_uid}
                  sx={{
                    position: 'relative',
                    minHeight: '190px',
                    maxHeight: '200px',
                  }}
                >
                  <Box sx={styles.artworkWrapper}>
                    <NextImage
                      src={el.image_medium}
                      layout="fill"
                      placeholder="blur"
                      blurDataURL={blurDataURL}
                      objectFit="cover"
                    />
                  </Box>
                </Grid>
              )
            }
          })}
        </Grid>
        <Box sx={styles.avatar}>
          <NextImage
            src={image}
            width="80px"
            height="80px"
            placeholder="blur"
            blurDataURL={blurDataURL}
          />
        </Box>
      </Box>

      <Box
        sx={{
          height: '20%',
        }}
      >
        <Box sx={styles.infoWrapper}>
          <Typography variant="subtitle2" sx={styles.name}>
            {`${first_name} ${last_name}`}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <LocationIcon />
            <Box
              sx={{
                marginLeft: '0.375rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.25rem',
                flexWrap: 'wrap',
              }}
            >
              <Box sx={styles.locationsContainer}>
                <Typography variant="bodyLato2" sx={styles.location}>
                  {locations}
                </Typography>
              </Box>
            </Box>
          </Box>
          <Box sx={styles.stylesWrapper}>
            {tattooStyles.map((el, idx) => (
              <Chip
                label={el.name}
                key={`${idx}-${el.name}`}
                variant="outlined"
              />
            ))}
          </Box>
        </Box>
      </Box>
      {/* <Box sx={styles.artist}>
        <Box sx={styles.avatar}>
          <NextImage
            src={image}
            width="80px"
            height="80px"
            placeholder="blur"
            blurDataURL={blurDataURL}
          />
        </Box>
        <Box sx={styles.infoWrapper}>
          <Typography variant="h6" sx={styles.name}>
            {`${first_name} ${last_name}`}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <LocationIcon />
            <Box
              sx={{
                marginLeft: '0.375rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.25rem',
                flexWrap: 'wrap',
              }}
            >
              <Box sx={styles.locationsContainer}>
                <Typography variant="bodyLato2" sx={styles.location}>
                  {locations}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>

      <Box sx={styles.verticalDivider} />
      <Box sx={styles.stylesWrapper}>
        {tattooStyles.map((el, idx) => (
          <Chip label={el.name} key={`${idx}-${el.name}`} variant="outlined" />
        ))}
      </Box>
      <Box sx={styles.verticalDivider} />
      <Box sx={styles.artworks}>
        {tattoo.map((el, idx) => {
          if (idx < 3) {
            return (
              <Box sx={styles.artworkWrapper} key={el.tattoo_uid}>
                <NextImage
                  src={el.image_medium}
                  height="125px"
                  width="125px"
                  placeholder="blur"
                  blurDataURL={blurDataURL}
                />
              </Box>
            )
          }
        })}
      </Box>
      {variant !== 'hero' && variant !== 'small' && (
        <Box sx={styles.buttonContainer}>
          <Button variant="outlined">Discover artist</Button>
        </Box>
      )} */}
    </Card>
  )

  return slug || variant !== 'hero' ? (
    <Link href={`artists/${slug}`}>
      <a>
        <CardHTML />
      </a>
    </Link>
  ) : (
    <CardHTML />
  )
}

export default ArtistCard
